import * as React from "react"
import {InjectedIntl} from "react-intl"
import {Col} from "@irati/ui/src/Layout"

import {InjectIntl, injectIntl} from "@irati/utils/lang"
import Validation, {StringSchema} from "@irati/utils/validation"
import {getMaxDay, getDaysForSelect, getMonthsForSelect, getYearsForSelect, setDate} from "@irati/utils/dob"
import IconText from "../IconText"
import {IconTypes} from "../Icon"
import {Day, Month, Year} from "./styled"

import FormError from "@irati/ui/src/FormItem/FormError"
import {FormGroup} from "@irati/ui/src/Form"
import {Select} from "@irati/ui/src/FormItem"

interface OptionalProps {
  value?: string | null
  onChange: (value: string | null) => void
  dayMonthRequired?: boolean
  dayMonthYearRequired?: boolean
  isMinimumAge18?: boolean
  excludeYear?: boolean
  disabled?: boolean
  validation?: StringSchema
  hint?: string
}
interface Props extends OptionalProps, InjectIntl {
  label: string
  intl: InjectedIntl
}

interface State {
  day: string | null
  month: string | null
  year: string | number | null
  isValid: boolean
}

class DOBSelect extends React.PureComponent<Props, State> {
  static defaultProps: OptionalProps = {
    value: null,
    onChange: () => {},
    dayMonthRequired: false,
    dayMonthYearRequired: false,
    isMinimumAge18: false,
    excludeYear: false
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      ...setDate(props.value),
      isValid: true
    }
  }

  updateDOB = (label: "day" | "month" | "year", value: string | number | null) => {
    this.setState({[label]: value || ""}, () => {
      const {onChange} = this.props
      let {day} = this.state
      const {month, year} = this.state
      const maxDay = getMaxDay(year, month)
      if (day && maxDay < day) {
        day = ""
        this.setState({day})
      }
      if (year === "" && month === "" && day === "") {
        onChange(null)
      } else if (year) {
        onChange(`${year}-${month}-${day}`)
      } else {
        onChange(`${month}-${day}`)
      }
    })
  }

  validate = () => {
    const {day, month, year} = this.state
    if (this.props.dayMonthYearRequired || year) {
      // if `dayMonthYearRequired` or `year` set, we need all
      return year && month && day
    } else if (this.props.dayMonthRequired || month || day) {
      // if `dayMonthRequired`, `month` or `day` are set, we need both day and month
      return month && day
    }

    // if nothing set, we're good, optional field
    return true
  }

  validationMessage = () => {
    const {dayMonthYearRequired, excludeYear, formatMessage} = this.props
    if (dayMonthYearRequired) {
      return formatMessage({id: "account.formError.formItem.validation.validFullDate"})
    } else if (excludeYear) {
      return formatMessage({id: "account.formError.formItem.validation.validDateWithoutYear"})
    }
    return formatMessage({id: "account.formError.formItem.validation.validDate"})
  }

  reset = (value: string) => {
    this.setState({
      ...setDate(value)
    })
  }

  render() {
    const {label, dayMonthRequired, dayMonthYearRequired, validation, excludeYear, isMinimumAge18, intl, hint, disabled, formatMessage} =
      this.props
    const {day, year, month} = this.state

    const {formatDate} = intl
    const dayOptions = getDaysForSelect(year, month)
    const monthOptions = getMonthsForSelect(formatDate)
    const yearOptions = getYearsForSelect(isMinimumAge18)
    const currentMonth = month && monthOptions.find((monthForSelect) => monthForSelect.value === month)

    const formValidation =
      validation ||
      Validation.string((dayMonthRequired || dayMonthYearRequired) && "platform.defaultValidation.required").test(
        "dob",
        this.validationMessage(),
        this.validate
      )
    return (
      <FormGroup>
        <Col xs={12}>
          <label htmlFor="day">{label}</label>
        </Col>
        <Day xs={excludeYear ? 6 : 4} paddingRight={!excludeYear} marginBottom={!hint}>
          <Select
            isDisabled={disabled}
            name="day"
            placeholder={formatMessage({id: "platform.fields.dateOfBirth.placeholders.day"})}
            value={day && dayOptions.find(({value = ""}) => value === day)}
            aria-label="Day"
            options={dayOptions}
            isClearable={false}
            onChange={(value: string) => this.updateDOB("day", value)}
          />
        </Day>
        <Month xs={excludeYear ? 6 : 4} paddingRight={!excludeYear} marginBottom={!hint}>
          <Select
            isDisabled={disabled}
            name="month"
            placeholder={formatMessage({id: "platform.fields.dateOfBirth.placeholders.month"})}
            value={currentMonth}
            aria-label="Month"
            options={monthOptions}
            isClearable={false}
            onChange={(value: string) => this.updateDOB("month", value)}
          />
        </Month>
        {!excludeYear && (
          <Year xs={4} marginBottom={!hint}>
            <Select
              isDisabled={disabled}
              name="year"
              placeholder={formatMessage({id: "platform.fields.dateOfBirth.placeholders.year"})}
              value={year && {value: year, label: year}}
              aria-label="Year"
              options={yearOptions}
              isClearable={false}
              onChange={(value: string | number) => this.updateDOB("year", value)}
            />
          </Year>
        )}
        <FormError validation={formValidation} value={this.props.value} />
        <Col xs={12}>
          {hint && (
            <div>
              <IconText icon={IconTypes.info} small marginTop>
                {hint}
              </IconText>
            </div>
          )}
        </Col>
      </FormGroup>
    )
  }
}

export default injectIntl(DOBSelect, {withRef: true})
